// @ts-nocheck — this is a behavior-preserving port of the established guard; typed seams are local.
import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from './authHooks';
import { Navigate, useLocation } from 'react-router-dom';
import { ProductRecoveryScreen } from '../errors/ProductRouteStates';
import { isAccessPendingError } from '../../../lib/accessPendingRedirect';
import {
    CHOOSE_ROLE_PATH,
    FAMILY_HOME_PATH,
    resolveSignedInDestination,
    shouldRedirectGuardianToFamily,
    shouldRedirectNonGuardianFromFamily,
    shouldRedirectToChooseRole,
    shouldRedirectTypedUserFromChooseRole,
    isChooseRoleSuppressed,
} from '../family/familyAccountHome';
import {
    useCanonicalProductPath,
    useProductDeploymentProfile,
} from '../navigation/ProductNavigation';
import { resolveCandidateProductAccess } from './productAccessPolicy';

const SSO_SESSION_CHECK_TIMEOUT_MS = 10_000;

export const ProtectedRoute = ({ children }) => {
    // We trust our internal AuthContext 'user' state as the source of truth for app access
    // Auth0 is just an external provider that feeds into it.
    const { user, loading, verifying, profileUnavailable, handleExternalLogin } = useAuth();
    const { isLoading: auth0Loading, isAuthenticated, getAccessTokenSilently } = useAuth0();
    const location = useLocation();
    const canonicalProductPath = useCanonicalProductPath();
    const deploymentProfile = useProductDeploymentProfile();
    const accountPolicyPath = location.pathname === canonicalProductPath(CHOOSE_ROLE_PATH)
        ? CHOOSE_ROLE_PATH
        : location.pathname;
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncAttempted, setSyncAttempted] = useState(false);
    const [syncFailed, setSyncFailed] = useState(false);
    const syncStarted = useRef(false);
    const mounted = useRef(true);

    useEffect(() => () => { mounted.current = false; }, []);

    // Effect: Sync Auth0 session to Internal Session if needed
    useEffect(() => {
        const syncSession = async () => {
            if (isAuthenticated && !user && !auth0Loading && !loading && !syncStarted.current) {
                syncStarted.current = true;
                let timeoutId;
                try {
                    console.log('[ProtectedRoute] Auth0 authenticated but no internal session. Syncing...');
                    setIsSyncing(true);
                    const sync = (async () => {
                        const token = await getAccessTokenSilently();
                        await handleExternalLogin(token, `${location.pathname}${location.search}${location.hash}`);
                    })();
                    const timeout = new Promise((_, reject) => {
                        timeoutId = window.setTimeout(
                            () => reject(new Error('SSO session check timed out')),
                            SSO_SESSION_CHECK_TIMEOUT_MS,
                        );
                    });
                    await Promise.race([sync, timeout]);
                    console.log('[ProtectedRoute] Sync successful.');
                } catch (error) {
                    console.error('[ProtectedRoute] Session sync failed:', error);
                    if (!isAccessPendingError(error) && mounted.current) setSyncFailed(true);
                } finally {
                    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
                    if (mounted.current) {
                        setIsSyncing(false);
                        setSyncAttempted(true);
                    }
                }
            }
        };
        syncSession();
    }, [isAuthenticated, user, auth0Loading, loading, getAccessTokenSilently, handleExternalLogin, location.pathname, location.search, location.hash]);

    // A failed or timed-out SSO attempt owns the screen even if its uncancellable
    // underlying request settles later. Retry starts a clean document-level check.
    if (syncFailed) return <ProductRecoveryScreen kind="session-check-failure" />;

    // A profile read failure is UNKNOWN even when a cached adapter retained an
    // email. Never interpret a partial object as an authoritative untyped user.
    if (user && profileUnavailable) return <ProductRecoveryScreen kind="api-unavailable" />;

    // 1. Verified session -> Render Page.
    // `user.email` is only set AFTER /api/users/me returns 200, i.e. the access gate
    // has been cleared. We deliberately do NOT render on a token-only optimistic user
    // here: doing so flashes the gated content for a beat before the async 403
    // access_pending redirect fires (the bug this guards against). A not-yet-allowlisted
    // user is held on the spinner below until AuthContext redirects to /access-pending.
    if (user?.email) {
        if (deploymentProfile === 'www-candidate') {
            const resolution = resolveCandidateProductAccess({
                user,
                pathname: location.pathname,
                chooseRoleSuppressed: isChooseRoleSuppressed(user),
            });
            if (resolution.kind === 'redirect') {
                return <Navigate to={canonicalProductPath(resolution.destination)} replace state={{ from: location }} />;
            }
            if (resolution.kind === 'recover') {
                const kind = resolution.reason === 'no-usable-area'
                    ? 'product-access-empty'
                    : 'product-access-invalid';
                return <ProductRecoveryScreen kind={kind} />;
            }
            return children;
        }
        if (shouldRedirectGuardianToFamily(user, accountPolicyPath)) {
            return <Navigate to={canonicalProductPath(FAMILY_HOME_PATH)} replace />;
        }
        if (shouldRedirectNonGuardianFromFamily(user, accountPolicyPath)) {
            return <Navigate to={canonicalProductPath(resolveSignedInDestination(user))} replace />;
        }
        if (shouldRedirectToChooseRole(user, accountPolicyPath)) {
            return <Navigate to={canonicalProductPath(CHOOSE_ROLE_PATH)} replace state={{ from: location }} />;
        }
        if (shouldRedirectTypedUserFromChooseRole(user, accountPolicyPath)) {
            return <Navigate to={canonicalProductPath(resolveSignedInDestination(user))} replace />;
        }
        return children;
    }

    // 2. Loading / verifying states (initial gate check, sync, or Auth0 init).
    //    `verifying` is true while the boot-time /api/users/me is in flight for a stored
    //    token — wait for it so an inactive user never sees gated content.
    const ssoCheckPending = isAuthenticated && !user && !syncAttempted;
    if (verifying || loading || auth0Loading || isSyncing || ssoCheckPending) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen" role="status" aria-live="polite">
                <div aria-hidden="true" className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-slate-500 text-sm font-medium">{(auth0Loading || isSyncing || ssoCheckPending) ? 'Checking your session...' : 'Checking your access...'}</p>
            </div>
        );
    }

    // 3. Fallback: If isAuthenticated but Sync Failed (syncAttempted=true) -> Show Error & Retry
    if (isAuthenticated && syncAttempted && !user) {
        return <ProductRecoveryScreen kind="session-check-failure" />;
    }

    // Defensive token-only fallback. This branch is not reachable after a
    // settled profile request, but retaining an honest recovery state prevents
    // an accidental access grant if a future auth adapter omits the flag.
    if (user) return <ProductRecoveryScreen kind="api-unavailable" />;

    // 5. Not Authenticated -> Redirect to Login
    console.log('[ProtectedRoute] Access denied, redirecting to login');
    return <Navigate to={canonicalProductPath('/login')} state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }} replace />;
};
